import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'grid', 'legend', 'jobRow', 'taskRow',
    'equipmentFilterButton', 'equipmentFilterDropdown', 'equipmentFilterCheckbox', 'equipmentFilterCount', 'equipmentFilterSearch',
    'peopleFilterButton', 'peopleFilterDropdown', 'peopleFilterCheckbox', 'peopleFilterCount', 'peopleFilterSearch',
    'locationFilterButton', 'locationFilterDropdown', 'locationFilterCheckbox', 'locationFilterCount', 'locationFilterSearch',
    'flagChip', 'filterStatus', 'recalcStatus', 'refreshPrompt', 'queueCount'
  ];

  initialize() {
    this.currentPopup = null;
    this.currentTaskId = null;
    this.currentPoolType = null;
  }

  connect() {
    this.initializeSortable();
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this);
    document.addEventListener('click', this.boundCloseOnClickOutside);
    this.createPopupElement();
    this.loadFiltersFromStorage();
    this.restoreExpandedJobs();

    // Resume watching a background recalculation that was pending before a reload
    this.resumeRecalcWatchIfPending();

    // Watch for recalculations this session didn't cause (other users, task
    // completions elsewhere, the daily refresh run)
    this.startAmbientWatch();
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseOnClickOutside);
    this.removePopupElement();
    clearTimeout(this.recalcTimer);
    clearInterval(this.ambientTimer);
  }

  createPopupElement() {
    this.popupElement = document.createElement('div');
    // max-w-xs keeps long helper text wrapping instead of stretching the popup
    this.popupElement.className = 'hidden fixed bg-white border rounded-lg shadow-lg min-w-48 max-w-xs';
    this.popupElement.style.zIndex = '9999';
    document.body.appendChild(this.popupElement);
  }

  removePopupElement() {
    if (this.popupElement && this.popupElement.parentNode) {
      this.popupElement.parentNode.removeChild(this.popupElement);
    }
  }

  // ----- job expand / collapse -----
  // Rollup rows render from job columns alone; task rows are fetched from
  // the job_tasks endpoint on first expand and removed on collapse. The
  // expanded set persists in localStorage so a reload keeps your place.

  get expandedStorageKey() {
    return 'automated_schedule_expanded_jobs';
  }

  expandedJobIds() {
    try {
      return JSON.parse(localStorage.getItem(this.expandedStorageKey)) || [];
    } catch (e) {
      return [];
    }
  }

  rememberExpansion(jobId, expanded) {
    var ids = this.expandedJobIds().filter(id => String(id) !== String(jobId));
    if (expanded) ids.push(String(jobId));
    localStorage.setItem(this.expandedStorageKey, JSON.stringify(ids));
  }

  restoreExpandedJobs() {
    if (!this.hasGridTarget) return;
    var ids = this.expandedJobIds();
    this.jobRowTargets.forEach(row => {
      if (ids.includes(String(row.dataset.jobId))) {
        this.expandJob(row);
      }
    });
  }

  toggleJob(event) {
    // Links inside the row (job name) navigate; chips/popups stop their own events
    if (event.target.closest('a')) return;
    const row = event.currentTarget;
    if (row.classList.contains('expanded')) {
      this.collapseJob(row);
    } else {
      this.expandJob(row);
    }
  }

  expandJob(row) {
    if (row.dataset.loading || row.classList.contains('expanded')) return;
    row.dataset.loading = 'true';
    const grid = this.gridTarget;

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/job_tasks',
      data: {
        job_id: row.dataset.jobId,
        zoom: grid.dataset.zoom,
        from: grid.dataset.anchor
      },
      dataType: 'html',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        delete row.dataset.loading;
        if (row.classList.contains('expanded')) return;
        row.insertAdjacentHTML('afterend', html);
        row.classList.add('expanded');
        this.rememberExpansion(row.dataset.jobId, true);
        this.applyFilters();
      },
      error: (error) => {
        delete row.dataset.loading;
        console.error('Error loading job tasks:', error);
      }
    });
  }

  collapseJob(row) {
    document.querySelectorAll('[data-parent-job-id="' + row.dataset.jobId + '"]').forEach(el => el.remove());
    row.classList.remove('expanded');
    this.rememberExpansion(row.dataset.jobId, false);
  }

  toggleLegend(event) {
    event.stopPropagation();
    if (this.hasLegendTarget) this.legendTarget.classList.toggle('hidden');
  }

  initializeSortable() {
    // Sortable is initialized when the queue panel first opens (see initializeQueueSortable)
  }

  // Schedule Queue panel (replaces the old Manage Jobs modal — jobs now join
  // the schedule automatically via schedulable job statuses)
  get queuePanel() {
    return document.getElementById('schedule-queue-panel');
  }

  isQueuePanelOpen() {
    const panel = this.queuePanel;
    return panel && !panel.classList.contains('hidden');
  }

  openQueuePanel() {
    const panel = this.queuePanel;
    if (!panel) return;
    panel.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    this.initializeQueueSortable();
    this.bindQueueEvents(panel);
    this.boundEscapeHandler = (e) => { if (e.key === 'Escape') this.closeQueuePanel(); };
    document.addEventListener('keydown', this.boundEscapeHandler);
  }

  closeQueuePanel() {
    const panel = this.queuePanel;
    if (!panel) return;
    panel.classList.add('hidden');
    document.body.style.overflow = '';
    if (this.boundEscapeHandler) {
      document.removeEventListener('keydown', this.boundEscapeHandler);
      this.boundEscapeHandler = null;
    }
    // Refresh the gantt table once the background recalculation has finished
    if (this.queueNeedsRefresh && this.recalcFinished) {
      window.location.reload();
    }
  }

  initializeQueueSortable() {
    const that = this;
    const panel = this.queuePanel;
    if (!panel) return;

    const listEl = panel.querySelector('[data-queue-list]');
    if (!listEl || listEl.dataset.sortableInit) return;
    listEl.dataset.sortableInit = 'true';

    $(listEl).sortable({
      items: '> [data-job-id]',
      cursor: 'move',
      placeholder: 'bg-blue-100 border-2 border-dashed border-blue-300 h-16 mx-4 my-1 rounded',
      tolerance: 'pointer',
      update: function(event, ui) {
        const jobId = ui.item.data('job-id');
        const newIndex = ui.item.index();
        that.updatePriorityNumbers();
        that.updateJobPriority(jobId, newIndex + 1);
      }
    });
  }

  bindQueueEvents(panel) {
    // Only bind once
    if (panel.dataset.bound) return;
    panel.dataset.bound = 'true';

    panel.querySelector('[data-queue-backdrop]').addEventListener('click', () => this.closeQueuePanel());
    panel.querySelectorAll('[data-queue-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeQueuePanel());
    });

    // Hold / resume buttons — delegated so rows keep working after moving
    // between the Scheduled and On Hold sections
    panel.addEventListener('click', (e) => {
      const holdBtn = e.target.closest('[data-queue-hold-btn]');
      if (holdBtn) {
        e.stopPropagation();
        this.updateJobScheduled(holdBtn.closest('[data-job-id]').dataset.jobId, false);
        return;
      }
      const resumeBtn = e.target.closest('[data-queue-resume-btn]');
      if (resumeBtn) {
        e.stopPropagation();
        this.updateJobScheduled(resumeBtn.closest('[data-job-id]').dataset.jobId, true);
      }
    });

    // Client-side search across both sections — all space-separated terms must match
    const search = panel.querySelector('[data-queue-search]');
    if (search) {
      search.addEventListener('keyup', (e) => {
        const terms = e.target.value.toLowerCase().split(/\s+/).filter(t => t.length > 0);
        panel.querySelectorAll('[data-queue-item]').forEach(item => {
          const searchData = item.dataset.jobSearch || '';
          const matches = terms.every(term => searchData.includes(term));
          item.classList.toggle('hidden', !matches);
        });
      });
    }

    // Earliest start date inputs
    panel.querySelectorAll('[data-queue-earliest-start]').forEach(input => {
      input.addEventListener('change', (e) => this.updateEarliestStart(e));
    });

    // Due (requested completion) date inputs
    panel.querySelectorAll('[data-queue-due]').forEach(input => {
      input.addEventListener('change', (e) => this.updateRequestedCompletion(e));
    });
  }

  updateJobScheduled(jobId, isScheduled) {
    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_job_scheduled',
      data: {
        job_id: jobId,
        is_scheduled: isScheduled,
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (!data.success) return;
        this.moveQueueItem(jobId, !isScheduled);
        this.startRecalcWatch(data.last_scheduled_at);
      },
      error: (error) => {
        console.error('Error updating job scheduled status:', error);
        window.location.reload();
      }
    });
  }

  // Moves a row between the Scheduled and On Hold sections and swaps which
  // action button shows — both buttons are always present in the row markup.
  moveQueueItem(jobId, toHeld) {
    const panel = this.queuePanel;
    if (!panel) return;
    const item = panel.querySelector(`[data-queue-item][data-job-id="${jobId}"]`);
    if (!item) return;

    const scheduledList = panel.querySelector('[data-queue-list]');
    const heldList = panel.querySelector('[data-queue-held-list]');
    const holdBtn = item.querySelector('[data-queue-hold-btn]');
    const resumeBtn = item.querySelector('[data-queue-resume-btn]');
    const priorityEl = item.querySelector('[data-priority-number]');

    item.dataset.held = String(toHeld);
    if (holdBtn) holdBtn.classList.toggle('hidden', toHeld);
    if (resumeBtn) resumeBtn.classList.toggle('hidden', !toHeld);
    if (priorityEl) priorityEl.classList.toggle('hidden', toHeld);

    if (toHeld) {
      heldList.appendChild(item);
    } else {
      // Resumed jobs join the end of the queue (matches next_priority server-side)
      scheduledList.appendChild(item);
    }

    const $scheduled = $(scheduledList);
    if ($scheduled.sortable('instance')) $scheduled.sortable('refresh');

    this.updatePriorityNumbers();
    this.updateQueueCounts();
  }

  updateQueueCounts() {
    const panel = this.queuePanel;
    if (!panel) return;
    const scheduledCount = panel.querySelectorAll('[data-queue-list] [data-queue-item]').length;
    const heldCount = panel.querySelectorAll('[data-queue-held-list] [data-queue-item]').length;

    const scheduledEl = panel.querySelector('[data-queue-scheduled-count]');
    const heldEl = panel.querySelector('[data-queue-held-count]');
    if (scheduledEl) scheduledEl.textContent = scheduledCount;
    if (heldEl) heldEl.textContent = heldCount;

    if (this.hasQueueCountTarget) this.queueCountTarget.textContent = scheduledCount;

    panel.querySelectorAll('[data-queue-empty]').forEach(el => {
      const list = el.dataset.queueEmpty === 'held'
        ? panel.querySelector('[data-queue-held-list]')
        : panel.querySelector('[data-queue-list]');
      el.classList.toggle('hidden', list.querySelectorAll('[data-queue-item]').length > 0);
    });
  }

  updateJobPriority(jobId, newPriority) {
    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_job_priority',
      data: {
        job_id: jobId,
        priority: newPriority,
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          this.startRecalcWatch(data.last_scheduled_at);
        }
      },
      error: (error) => {
        console.error('Error updating job priority:', error);
        window.location.reload();
      }
    });
  }

  updatePriorityNumbers() {
    const panel = this.queuePanel;
    if (!panel) return;
    const list = panel.querySelector('[data-queue-list]');
    if (!list) return;
    // Use only direct children with data-job-id to avoid stale references
    let num = 1;
    for (const child of list.children) {
      if (!child.dataset.jobId) continue;
      const numEl = child.querySelector('[data-priority-number]');
      if (numEl) numEl.textContent = num;
      num++;
    }
  }

  updateEarliestStart(evt) {
    const input = evt.target;
    const jobId = $(input).data('job-id');
    const earliestStart = input.value;

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_earliest_start',
      data: {
        job_id: jobId,
        earliest_start: earliestStart,
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          this.startRecalcWatch(data.last_scheduled_at);
        }
      },
      error: (error) => {
        console.error('Error updating earliest start:', error);
      }
    });
  }

  updateRequestedCompletion(evt) {
    const input = evt.target;
    const jobId = $(input).data('job-id');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_requested_completion',
      data: {
        job_id: jobId,
        requested_completion: input.value,
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          // A due-date change doesn't trigger a recalculation — the schedule
          // is unchanged; only the at-risk flags need a re-render on close.
          this.queueNeedsRefresh = true;
          this.recalcFinished = true;
        }
      },
      error: (error) => {
        console.error('Error updating requested completion:', error);
      }
    });
  }

  runScheduler() {
    $.ajax({
      method: 'POST',
      url: '/automated_schedules/run_scheduler',
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          this.startRecalcWatch(data.last_scheduled_at);
        }
      },
      error: (error) => {
        console.error('Error running scheduler:', error);
      }
    });
  }

  // ----- background recalculation watch -----
  // Schedule mutations enqueue a debounced background run; we poll
  // schedule_status until last_scheduled_at moves past the baseline captured
  // from the mutation response, then refresh (immediately, or on panel close
  // so drag sessions aren't interrupted). The baseline lives in sessionStorage
  // so a reload mid-recalculation resumes the watch and still picks up the
  // final result.

  get recalcStorageKey() {
    return 'automated_schedule_recalc_baseline';
  }

  startRecalcWatch(baseline) {
    sessionStorage.setItem(this.recalcStorageKey, baseline || 'null');
    this.queueNeedsRefresh = true;
    this.recalcFinished = false;
    this.setRecalcStatus('Recalculating schedule...');
    this.recalcAttempts = 0;
    clearTimeout(this.recalcTimer);
    this.recalcTimer = setTimeout(() => this.pollRecalcStatus(), 2000);
  }

  resumeRecalcWatchIfPending() {
    if (sessionStorage.getItem(this.recalcStorageKey) === null) return;
    this.queueNeedsRefresh = true;
    this.recalcFinished = false;
    this.setRecalcStatus('Recalculating schedule...');
    this.recalcAttempts = 0;
    this.recalcTimer = setTimeout(() => this.pollRecalcStatus(), 2000);
  }

  pollRecalcStatus() {
    this.recalcAttempts++;
    if (this.recalcAttempts > 90) { // give up after ~3 minutes
      sessionStorage.removeItem(this.recalcStorageKey);
      this.setRecalcStatus('');
      return;
    }

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/schedule_status',
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        const stored = sessionStorage.getItem(this.recalcStorageKey);
        const baseline = stored === 'null' ? null : stored;
        if (data.last_scheduled_at && data.last_scheduled_at !== baseline) {
          this.finishRecalcWatch();
        } else {
          this.recalcTimer = setTimeout(() => this.pollRecalcStatus(), 2000);
        }
      },
      error: () => {
        this.recalcTimer = setTimeout(() => this.pollRecalcStatus(), 5000);
      }
    });
  }

  finishRecalcWatch() {
    sessionStorage.removeItem(this.recalcStorageKey);
    this.recalcFinished = true;
    if (this.isQueuePanelOpen()) {
      this.setRecalcStatus('Schedule updated. The page will refresh when you close the queue.');
    } else {
      window.location.reload();
    }
  }

  setRecalcStatus(message) {
    if (this.hasRecalcStatusTarget) {
      this.recalcStatusTarget.textContent = message;
    }
  }

  // ----- ambient staleness watch -----
  // The mutation watch above only covers recalculations this session caused.
  // Model hooks also debounce runs from everywhere else (task completions on
  // the job page, downtime edits, other users' drags, the daily refresh) —
  // this slow poll compares the server's last_scheduled_at to the value this
  // page rendered from and, when it moves, offers a quiet refresh prompt
  // instead of yanking the page out from under the user.

  startAmbientWatch() {
    if (!this.hasRecalcStatusTarget) return;
    this.renderBaseline = this.recalcStatusTarget.dataset.baseline || null;
    this.ambientTimer = setInterval(() => this.ambientTick(), 60000);
  }

  ambientTick() {
    // A pending mutation watch polls faster and reloads on its own
    if (sessionStorage.getItem(this.recalcStorageKey) !== null) return;

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/schedule_status',
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (!data.last_scheduled_at) return;
        if (data.last_scheduled_at !== this.renderBaseline) {
          this.showRefreshPrompt();
        } else {
          this.updateCalculatedAgo(data.last_scheduled_at);
        }
      }
    });
  }

  showRefreshPrompt() {
    clearInterval(this.ambientTimer);
    this.setRecalcStatus('');
    if (this.hasRefreshPromptTarget) {
      this.refreshPromptTarget.classList.remove('hidden');
    }
  }

  refreshForUpdate() {
    window.location.reload();
  }

  // Keeps "Last calculated X ago" honest between polls (the server-rendered
  // text would otherwise stay frozen at page-load time)
  updateCalculatedAgo(iso) {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    let text;
    if (mins < 1) {
      text = 'Last calculated just now';
    } else if (mins < 60) {
      text = `Last calculated ${mins} minute${mins === 1 ? '' : 's'} ago`;
    } else {
      const hours = Math.round(mins / 60);
      text = `Last calculated ${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    this.setRecalcStatus(text);
  }

  openEquipmentPool(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'equipment';

    // Load the form content
    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_equipment_pool',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading equipment pool form:', error);
      }
    });
  }

  openPeoplePool(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'people';

    // Load the form content
    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_people_pool',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading people pool form:', error);
      }
    });
  }

  openTaskSchedule(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'schedule';

    // Load the form content
    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_schedule',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading task schedule form:', error);
      }
    });
  }

  openTaskStatus(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'status';

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_status',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading task status form:', error);
      }
    });
  }

  openPersonEffort(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'person_effort';

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_person_effort',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading person effort form:', error);
      }
    });
  }

  openEquipmentEffort(event) {
    event.stopPropagation();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    this.closeAllPopups();

    if (!this.popupElement) return;

    this.currentPopup = this.popupElement;
    this.currentTaskId = taskId;
    this.currentPoolType = 'equipment_effort';

    $.ajax({
      method: 'GET',
      url: '/automated_schedules/edit_task_equipment_effort',
      data: { task_id: taskId },
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (html) => {
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.positionPopup(this.popupElement, trigger);
        this.popupElement.classList.remove('hidden');
      },
      error: (error) => {
        console.error('Error loading equipment effort form:', error);
      }
    });
  }

  bindPopupEvents() {
    const cancelBtn = this.popupElement.querySelector('[data-action*="closePoolPopup"]');
    const saveBtn = this.popupElement.querySelector('[data-action*="save"]');
    const searchInput = this.popupElement.querySelector('[data-pool-search]');

    if (cancelBtn) {
      cancelBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeAllPopups();
      });
    }

    if (saveBtn) {
      saveBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.currentPoolType === 'equipment') {
          this.saveEquipmentPool(e);
        } else if (this.currentPoolType === 'people') {
          this.savePeoplePool(e);
        } else if (this.currentPoolType === 'schedule') {
          this.saveTaskSchedule(e);
        } else if (this.currentPoolType === 'status') {
          this.saveTaskStatus(e);
        } else if (this.currentPoolType === 'person_effort') {
          this.savePersonEffort(e);
        } else if (this.currentPoolType === 'equipment_effort') {
          this.saveEquipmentEffort(e);
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener('keyup', (e) => {
        this.filterPoolList(e.target.value);
      });
      // Focus the search input when popup opens
      searchInput.focus();
    }

    // Allow Enter key to submit from text inputs (effort fields, etc.)
    this.popupElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('input[type="text"]:not([data-pool-search])')) {
        e.preventDefault();
        const btn = this.popupElement.querySelector('[data-save-btn]');
        if (btn) btn.click();
      }
    });
  }

  filterPoolList(query) {
    const items = this.popupElement.querySelectorAll('[data-pool-item]');
    const lowerQuery = query.toLowerCase();

    items.forEach(item => {
      const name = item.dataset.name || '';
      if (name.includes(lowerQuery)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  reorderPoolItem(event) {
    const checkbox = event.target;
    const label = checkbox.closest('[data-pool-item]');
    const poolList = this.popupElement.querySelector('[data-pool-list]');
    const separator = poolList.querySelector('[data-pool-separator]');
    const isChecked = checkbox.checked;

    label.dataset.selected = isChecked ? 'true' : 'false';

    if (isChecked) {
      // Move above separator (or to top if no separator)
      if (separator) {
        poolList.insertBefore(label, separator);
      } else {
        // No separator exists, need to create one and add it
        const selectedItems = poolList.querySelectorAll('[data-pool-item][data-selected="true"]');
        const unselectedItems = poolList.querySelectorAll('[data-pool-item][data-selected="false"]');

        if (unselectedItems.length > 0) {
          const newSeparator = document.createElement('div');
          newSeparator.className = 'border-b border-gray-300 my-1';
          newSeparator.dataset.poolSeparator = '';
          poolList.insertBefore(label, unselectedItems[0]);
          poolList.insertBefore(newSeparator, unselectedItems[0]);
        } else {
          poolList.appendChild(label);
        }
      }
    } else {
      // Move below separator
      if (separator) {
        separator.after(label);
      }

      // Check if we need to remove the separator (no more selected items)
      const selectedItems = poolList.querySelectorAll('[data-pool-item][data-selected="true"]');
      if (selectedItems.length === 0 && separator) {
        separator.remove();
      }
    }
  }

  // The popup is position: fixed, so it works in viewport coordinates (no
  // scroll offsets). Prefers below the trigger; flips above when there's no
  // room, and clamps to the viewport as a last resort so it's never cut off.
  positionPopup(popup, trigger) {
    const rect = trigger.getBoundingClientRect();
    const margin = 8;

    // Measure while invisible — the callers unhide after positioning
    popup.style.visibility = 'hidden';
    popup.classList.remove('hidden');
    const popupHeight = popup.offsetHeight;
    const popupWidth = popup.offsetWidth;
    popup.style.visibility = '';

    let top = rect.bottom + 4;
    if (top + popupHeight > window.innerHeight - margin) {
      const above = rect.top - popupHeight - 4;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - popupHeight - margin);
    }

    let left = rect.left;
    if (left + popupWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - popupWidth - margin);
    }

    popup.style.top = `${top}px`;
    popup.style.left = `${left}px`;
  }

  saveEquipmentPool(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const checkboxes = this.currentPopup.querySelectorAll('input[name="equipment_ids[]"]:checked');
    const equipmentIds = Array.from(checkboxes).map(cb => cb.value);
    const tagCheckboxes = this.currentPopup.querySelectorAll('input[name="equipment_tags[]"]:checked');
    const equipmentTags = Array.from(tagCheckboxes).map(cb => cb.value);
    const equipmentEffortInput = this.currentPopup.querySelector('[data-task-equipment-effort]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_equipment_pool',
      data: {
        task_id: this.currentTaskId,
        equipment_ids: equipmentIds,
        equipment_tags: equipmentTags,
        auto_equipment_effort: equipmentEffortInput ? equipmentEffortInput.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating equipment pool:', error);
        this.hideSaveSpinner();
      }
    });
  }

  savePeoplePool(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const checkboxes = this.currentPopup.querySelectorAll('input[name="user_ids[]"]:checked');
    const userIds = Array.from(checkboxes).map(cb => cb.value);
    const tagCheckboxes = this.currentPopup.querySelectorAll('input[name="user_tags[]"]:checked');
    const userTags = Array.from(tagCheckboxes).map(cb => cb.value);
    const personEffortInput = this.currentPopup.querySelector('[data-task-person-effort]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_people_pool',
      data: {
        task_id: this.currentTaskId,
        user_ids: userIds,
        user_tags: userTags,
        auto_person_effort: personEffortInput ? personEffortInput.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating people pool:', error);
        this.hideSaveSpinner();
      }
    });
  }

  saveTaskSchedule(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const statusSelect = this.currentPopup.querySelector('[data-task-status]');
    const personEffortInput = this.currentPopup.querySelector('[data-task-person-effort]');
    const equipmentEffortInput = this.currentPopup.querySelector('[data-task-equipment-effort]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_schedule',
      data: {
        task_id: this.currentTaskId,
        status: statusSelect ? statusSelect.value : null,
        auto_person_effort: personEffortInput ? personEffortInput.value : null,
        auto_equipment_effort: equipmentEffortInput ? equipmentEffortInput.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating task schedule:', error);
        this.hideSaveSpinner();
      }
    });
  }

  saveTaskStatus(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const statusSelect = this.currentPopup.querySelector('[data-task-status]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_status',
      data: {
        task_id: this.currentTaskId,
        status: statusSelect ? statusSelect.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating task status:', error);
        this.hideSaveSpinner();
      }
    });
  }

  savePersonEffort(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const personEffortInput = this.currentPopup.querySelector('[data-task-person-effort]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_person_effort',
      data: {
        task_id: this.currentTaskId,
        auto_person_effort: personEffortInput ? personEffortInput.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating person effort:', error);
        this.hideSaveSpinner();
      }
    });
  }

  saveEquipmentEffort(event) {
    event.stopPropagation();
    if (!this.currentPopup || !this.currentTaskId) return;

    this.showSaveSpinner();

    const equipmentEffortInput = this.currentPopup.querySelector('[data-task-equipment-effort]');

    $.ajax({
      method: 'PATCH',
      url: '/automated_schedules/update_task_equipment_effort',
      data: {
        task_id: this.currentTaskId,
        auto_equipment_effort: equipmentEffortInput ? equipmentEffortInput.value : null
      },
      dataType: 'json',
      headers: { 'X-CSRF-Token': Rails.csrfToken() },
      success: (data) => {
        if (data.success) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.error('Error updating equipment effort:', error);
        this.hideSaveSpinner();
      }
    });
  }

  showSaveSpinner() {
    if (!this.currentPopup) return;
    const spinner = this.currentPopup.querySelector('[data-spinner]');
    const saveText = this.currentPopup.querySelector('[data-save-text]');
    const saveBtn = this.currentPopup.querySelector('[data-save-btn]');
    if (spinner) spinner.classList.remove('hidden');
    if (saveText) saveText.textContent = 'Saving...';
    if (saveBtn) saveBtn.disabled = true;
  }

  hideSaveSpinner() {
    if (!this.currentPopup) return;
    const spinner = this.currentPopup.querySelector('[data-spinner]');
    const saveText = this.currentPopup.querySelector('[data-save-text]');
    const saveBtn = this.currentPopup.querySelector('[data-save-btn]');
    if (spinner) spinner.classList.add('hidden');
    if (saveText) saveText.textContent = 'Save';
    if (saveBtn) saveBtn.disabled = false;
  }

  closePoolPopup(event) {
    event.stopPropagation();
    this.closeAllPopups();
  }

  closeAllPopups() {
    if (this.popupElement) {
      this.popupElement.classList.add('hidden');
      this.popupElement.innerHTML = '';
    }
    this.currentPopup = null;
    this.currentTaskId = null;
    this.currentPoolType = null;
  }

  closeOnClickOutside(event) {
    // Close pool popups
    if (this.currentPopup && !this.currentPopup.contains(event.target)) {
      this.closeAllPopups();
    }

    // Close the legend popover
    if (this.hasLegendTarget && !this.legendTarget.classList.contains('hidden') && !this.legendTarget.contains(event.target)) {
      this.legendTarget.classList.add('hidden');
    }

    // Close filter dropdowns if clicking outside
    if (this.hasEquipmentFilterDropdownTarget && !this.equipmentFilterDropdownTarget.classList.contains('hidden')) {
      const equipmentButton = this.hasEquipmentFilterButtonTarget ? this.equipmentFilterButtonTarget : null;
      if (!this.equipmentFilterDropdownTarget.contains(event.target) && (!equipmentButton || !equipmentButton.contains(event.target))) {
        this.equipmentFilterDropdownTarget.classList.add('hidden');
      }
    }

    if (this.hasPeopleFilterDropdownTarget && !this.peopleFilterDropdownTarget.classList.contains('hidden')) {
      const peopleButton = this.hasPeopleFilterButtonTarget ? this.peopleFilterButtonTarget : null;
      if (!this.peopleFilterDropdownTarget.contains(event.target) && (!peopleButton || !peopleButton.contains(event.target))) {
        this.peopleFilterDropdownTarget.classList.add('hidden');
      }
    }

    if (this.hasLocationFilterDropdownTarget && !this.locationFilterDropdownTarget.classList.contains('hidden')) {
      const locationButton = this.hasLocationFilterButtonTarget ? this.locationFilterButtonTarget : null;
      if (!this.locationFilterDropdownTarget.contains(event.target) && (!locationButton || !locationButton.contains(event.target))) {
        this.locationFilterDropdownTarget.classList.add('hidden');
      }
    }
  }

  showOverdueTooltip(event) {
    if (!this.popupElement) return;

    const trigger = event.currentTarget;
    this.popupElement.innerHTML = `
      <div class="p-2 text-xs text-gray-700">
        <strong class="text-red-600">Overdue:</strong> End date has passed but task is not complete. Please update the estimated duration.
      </div>
    `;
    this.positionPopup(this.popupElement, trigger);
    this.popupElement.classList.remove('hidden');
  }

  hideOverdueTooltip(event) {
    if (!this.popupElement) return;
    this.popupElement.classList.add('hidden');
    this.popupElement.innerHTML = '';
  }

  // Equipment filter methods
  toggleEquipmentFilter(event) {
    event.stopPropagation();
    if (this.hasEquipmentFilterDropdownTarget) {
      const isHidden = this.equipmentFilterDropdownTarget.classList.contains('hidden');
      this.closeAllFilterDropdowns();
      if (isHidden) {
        this.equipmentFilterDropdownTarget.classList.remove('hidden');
        if (this.hasEquipmentFilterSearchTarget) {
          this.equipmentFilterSearchTarget.focus();
        }
      }
    }
  }

  closeEquipmentFilter(event) {
    event.stopPropagation();
    if (this.hasEquipmentFilterDropdownTarget) {
      this.equipmentFilterDropdownTarget.classList.add('hidden');
    }
  }

  clearEquipmentFilter(event) {
    event.stopPropagation();
    this.equipmentFilterCheckboxTargets.forEach(cb => cb.checked = false);
    this.reorderAllFilterItems('equipment');
    this.applyFilters();
  }

  filterEquipmentList(event) {
    const query = event.target.value.toLowerCase();
    const dropdown = this.equipmentFilterDropdownTarget;
    const items = dropdown.querySelectorAll('[data-filter-item]');

    items.forEach(item => {
      const name = item.dataset.name || '';
      if (name.includes(query)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  // People filter methods
  togglePeopleFilter(event) {
    event.stopPropagation();
    if (this.hasPeopleFilterDropdownTarget) {
      const isHidden = this.peopleFilterDropdownTarget.classList.contains('hidden');
      this.closeAllFilterDropdowns();
      if (isHidden) {
        this.peopleFilterDropdownTarget.classList.remove('hidden');
        if (this.hasPeopleFilterSearchTarget) {
          this.peopleFilterSearchTarget.focus();
        }
      }
    }
  }

  closePeopleFilter(event) {
    event.stopPropagation();
    if (this.hasPeopleFilterDropdownTarget) {
      this.peopleFilterDropdownTarget.classList.add('hidden');
    }
  }

  clearPeopleFilter(event) {
    event.stopPropagation();
    this.peopleFilterCheckboxTargets.forEach(cb => cb.checked = false);
    this.reorderAllFilterItems('people');
    this.applyFilters();
  }

  filterPeopleList(event) {
    const query = event.target.value.toLowerCase();
    const dropdown = this.peopleFilterDropdownTarget;
    const items = dropdown.querySelectorAll('[data-filter-item]');

    items.forEach(item => {
      const name = item.dataset.name || '';
      if (name.includes(query)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  // Location filter methods
  toggleLocationFilter(event) {
    event.stopPropagation();
    if (this.hasLocationFilterDropdownTarget) {
      const isHidden = this.locationFilterDropdownTarget.classList.contains('hidden');
      this.closeAllFilterDropdowns();
      if (isHidden) {
        this.locationFilterDropdownTarget.classList.remove('hidden');
        if (this.hasLocationFilterSearchTarget) {
          this.locationFilterSearchTarget.focus();
        }
      }
    }
  }

  closeLocationFilter(event) {
    event.stopPropagation();
    if (this.hasLocationFilterDropdownTarget) {
      this.locationFilterDropdownTarget.classList.add('hidden');
    }
  }

  clearLocationFilter(event) {
    event.stopPropagation();
    this.locationFilterCheckboxTargets.forEach(cb => cb.checked = false);
    this.applyFilters();
  }

  filterLocationList(event) {
    const query = event.target.value.toLowerCase();
    const dropdown = this.locationFilterDropdownTarget;
    const items = dropdown.querySelectorAll('[data-filter-item]');

    items.forEach(item => {
      const name = item.dataset.name || '';
      if (name.includes(query)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  closeAllFilterDropdowns() {
    if (this.hasEquipmentFilterDropdownTarget) {
      this.equipmentFilterDropdownTarget.classList.add('hidden');
    }
    if (this.hasPeopleFilterDropdownTarget) {
      this.peopleFilterDropdownTarget.classList.add('hidden');
    }
    if (this.hasLocationFilterDropdownTarget) {
      this.locationFilterDropdownTarget.classList.add('hidden');
    }
  }

  getStorageKey() {
    return 'automated_schedule_filters';
  }

  // Attention chips: count pills that double as flag filters (OR within
  // chips, AND with the resource/location filters)
  toggleFlagFilter(event) {
    event.currentTarget.classList.toggle('on');
    this.applyFilters();
  }

  selectedFlagKeys() {
    return this.flagChipTargets
      .filter(chip => chip.classList.contains('on'))
      .map(chip => chip.dataset.flag);
  }

  saveFiltersToStorage() {
    const filters = {
      equipment: this.equipmentFilterCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value),
      people: this.peopleFilterCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value),
      locations: this.locationFilterCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value),
      flags: this.selectedFlagKeys()
    };
    localStorage.setItem(this.getStorageKey(), JSON.stringify(filters));
  }

  loadFiltersFromStorage() {
    const stored = localStorage.getItem(this.getStorageKey());
    if (!stored) return;

    try {
      const filters = JSON.parse(stored);

      // Apply equipment filter
      if (filters.equipment && filters.equipment.length > 0) {
        this.equipmentFilterCheckboxTargets.forEach(cb => {
          cb.checked = filters.equipment.includes(cb.value);
        });
      }

      // Apply people filter
      if (filters.people && filters.people.length > 0) {
        this.peopleFilterCheckboxTargets.forEach(cb => {
          cb.checked = filters.people.includes(cb.value);
        });
      }

      // Apply location filter
      if (filters.locations && filters.locations.length > 0) {
        this.locationFilterCheckboxTargets.forEach(cb => {
          cb.checked = filters.locations.includes(cb.value);
        });
      }

      // Apply attention-chip flags (a stored flag whose chip no longer
      // renders — count went to zero — just drops silently)
      if (filters.flags && filters.flags.length > 0) {
        this.flagChipTargets.forEach(chip => {
          chip.classList.toggle('on', filters.flags.includes(chip.dataset.flag));
        });
      }

      // Reorder filter items to show selected above the line
      this.reorderAllFilterItems('equipment');
      this.reorderAllFilterItems('people');

      // Apply the filters to show/hide rows
      this.applyFilters();
    } catch (e) {
      console.error('Error loading filters from storage:', e);
    }
  }

  handleFilterChange(event) {
    const checkbox = event.target;
    const filterType = checkbox.dataset.filterType;
    this.reorderFilterItem(checkbox, filterType);
    this.applyFilters();
  }

  reorderFilterItem(checkbox, filterType) {
    const label = checkbox.closest('[data-filter-item]');
    const filterList = document.querySelector(`[data-filter-list="${filterType}"]`);
    const separator = filterList.querySelector(`[data-filter-separator="${filterType}"]`);
    const isChecked = checkbox.checked;

    label.dataset.selected = isChecked ? 'true' : 'false';

    if (isChecked) {
      // Move above separator
      filterList.insertBefore(label, separator);
      separator.classList.remove('hidden');
    } else {
      // Move below separator
      separator.after(label);

      // Check if we need to hide the separator (no more selected items)
      const selectedItems = filterList.querySelectorAll('[data-filter-item][data-selected="true"]');
      if (selectedItems.length === 0) {
        separator.classList.add('hidden');
      }
    }
  }

  reorderAllFilterItems(filterType) {
    const filterList = document.querySelector(`[data-filter-list="${filterType}"]`);
    if (!filterList) return;

    const separator = filterList.querySelector(`[data-filter-separator="${filterType}"]`);
    const items = filterList.querySelectorAll('[data-filter-item]');

    const selectedItems = [];
    const unselectedItems = [];

    items.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        item.dataset.selected = 'true';
        selectedItems.push(item);
      } else {
        item.dataset.selected = 'false';
        unselectedItems.push(item);
      }
    });

    // Reorder: selected items first, then separator, then unselected
    selectedItems.forEach(item => filterList.insertBefore(item, separator));

    if (selectedItems.length > 0 && unselectedItems.length > 0) {
      separator.classList.remove('hidden');
    } else {
      separator.classList.add('hidden');
    }

    unselectedItems.forEach(item => filterList.appendChild(item));
  }

  applyFilters() {
    const selectedEquipmentIds = this.equipmentFilterCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    const selectedUserIds = this.peopleFilterCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    const selectedLocationIds = this.locationFilterCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    // Save filters to localStorage
    this.saveFiltersToStorage();

    // Update filter count badges
    this.updateFilterCounts(selectedEquipmentIds.length, selectedUserIds.length, selectedLocationIds.length);

    // Filter job rollup rows — a job stays visible when any of its tasks'
    // assigned equipment/person matches and any of its locations match.
    // (data-equipment-ids / data-user-ids aggregate the tasks server-side,
    // so collapsed rows filter without loading their task rows.)
    const selectedFlags = this.selectedFlagKeys();
    const resourceFilterActive = selectedEquipmentIds.length > 0 || selectedUserIds.length > 0;
    let visibleCount = 0;
    let totalCount = 0;
    const hiddenJobIds = new Set();

    this.jobRowTargets.forEach(row => {
      totalCount++;
      const jobEquipmentIds = (row.dataset.equipmentIds || '').split(',').filter(id => id);
      const jobUserIds = (row.dataset.userIds || '').split(',').filter(id => id);
      const jobLocationIds = (row.dataset.locationIds || '').split(',').filter(id => id);
      const jobFlags = (row.dataset.flags || '').split(',').filter(f => f);

      let showRow = true;

      if (selectedEquipmentIds.length > 0) {
        if (!selectedEquipmentIds.some(id => jobEquipmentIds.includes(id))) showRow = false;
      }

      if (selectedUserIds.length > 0) {
        if (!selectedUserIds.some(id => jobUserIds.includes(id))) showRow = false;
      }

      if (selectedLocationIds.length > 0) {
        if (!selectedLocationIds.some(id => jobLocationIds.includes(id))) showRow = false;
      }

      // Attention chips OR together: any selected problem keeps the job
      if (selectedFlags.length > 0) {
        if (!selectedFlags.some(f => jobFlags.includes(f))) showRow = false;
      }

      if (showRow) {
        row.classList.remove('hidden');
        visibleCount++;
      } else {
        row.classList.add('hidden');
        hiddenJobIds.add(String(row.dataset.jobId));
      }

      this.updateUsageSegments(row, showRow, resourceFilterActive, selectedEquipmentIds, selectedUserIds, selectedFlags);
    });

    // Expanded task rows: hidden with their job; otherwise matched
    // individually on their own assigned equipment/person. Location needs no
    // per-task check — task rows carry their job's locations.
    this.taskRowTargets.forEach(row => {
      if (hiddenJobIds.has(String(row.dataset.parentJobId))) {
        row.classList.add('hidden');
        return;
      }

      const rowEquipmentId = row.dataset.equipmentId || '';
      const rowUserId = row.dataset.userId || '';

      let showRow = true;

      if (selectedEquipmentIds.length > 0) {
        if (!selectedEquipmentIds.includes(rowEquipmentId)) showRow = false;
      }

      if (selectedUserIds.length > 0) {
        if (!selectedUserIds.includes(rowUserId)) showRow = false;
      }

      row.classList.toggle('hidden', !showRow);
    });

    // Filter queue panel job items by location
    const panel = this.queuePanel;
    if (panel) {
      panel.querySelectorAll('[data-queue-item]').forEach(item => {
        const itemLocationIds = (item.dataset.locationIds || '').split(',').filter(id => id);

        let showItem = true;

        if (selectedLocationIds.length > 0) {
          const hasMatchingLocation = selectedLocationIds.some(id => itemLocationIds.includes(id));
          if (!hasMatchingLocation) showItem = false;
        }

        item.classList.toggle('hidden', !showItem);
      });
    }

    // Update filter status
    this.updateFilterStatus(visibleCount, totalCount,
      selectedEquipmentIds.length + selectedUserIds.length + selectedLocationIds.length + selectedFlags.length);
  }

  // While a People/Equipment filter is on, a visible job's bar ghosts
  // (.asg-res-filtered) and the segments for its matching tasks show, so the
  // grid reads as "when is this person/equipment actually working" without
  // expanding rows. Date-less matches can't draw a segment — they surface
  // through the unscheduled note and the "X of Y tasks match" count instead.
  updateUsageSegments(row, showRow, resourceFilterActive, selectedEquipmentIds, selectedUserIds, selectedFlags) {
    row.classList.toggle('asg-res-filtered', resourceFilterActive && showRow);

    // at_risk is job-level — only task-level flags produce a "flagged" count
    const taskFlags = (selectedFlags || []).filter(f => f !== 'at_risk');

    let matchCount = 0;
    let visibleSegments = 0;
    let unscheduledMatches = 0;
    let flaggedCount = 0;

    row.querySelectorAll('[data-match-task]').forEach(seg => {
      let matches = resourceFilterActive;
      if (selectedEquipmentIds.length > 0 && !selectedEquipmentIds.includes(seg.dataset.equipmentId || '')) matches = false;
      if (selectedUserIds.length > 0 && !selectedUserIds.includes(seg.dataset.userId || '')) matches = false;

      if (matches) {
        matchCount++;
        if (seg.dataset.unscheduled === 'true') unscheduledMatches++;
      }
      if (taskFlags.length > 0) {
        const warnings = (seg.dataset.warnings || '').split(',').filter(w => w);
        if (taskFlags.some(f => warnings.includes(f))) flaggedCount++;
      }
      if (seg.classList.contains('asg-match-seg')) {
        const visible = matches && showRow;
        seg.classList.toggle('hidden', !visible);
        if (visible) visibleSegments++;
      }
    });

    const note = row.querySelector('[data-unsched-note]');
    if (note) {
      const showNote = resourceFilterActive && showRow && unscheduledMatches > 0 && visibleSegments === 0;
      note.textContent = showNote
        ? `${unscheduledMatches} matching ${unscheduledMatches === 1 ? 'task is' : 'tasks are'} unscheduled`
        : '';
      note.classList.toggle('hidden', !showNote);
    }

    const countEl = row.querySelector('[data-task-count]');
    if (countEl) {
      const total = parseInt(countEl.dataset.total, 10) || 0;
      const parts = [];
      if (resourceFilterActive) parts.push(`${matchCount} of ${total} tasks match`);
      if (taskFlags.length > 0 && flaggedCount > 0) parts.push(`${flaggedCount} of ${total} tasks flagged`);
      if (parts.length === 0) parts.push(`${total} ${total === 1 ? 'task' : 'tasks'}`);
      countEl.textContent = parts.join(' · ');
    }
  }

  updateFilterCounts(equipmentCount, peopleCount, locationCount) {
    if (this.hasEquipmentFilterCountTarget) {
      if (equipmentCount > 0) {
        this.equipmentFilterCountTarget.textContent = equipmentCount;
        this.equipmentFilterCountTarget.classList.remove('hidden');
      } else {
        this.equipmentFilterCountTarget.classList.add('hidden');
      }
    }

    if (this.hasPeopleFilterCountTarget) {
      if (peopleCount > 0) {
        this.peopleFilterCountTarget.textContent = peopleCount;
        this.peopleFilterCountTarget.classList.remove('hidden');
      } else {
        this.peopleFilterCountTarget.classList.add('hidden');
      }
    }

    if (this.hasLocationFilterCountTarget) {
      if (locationCount > 0) {
        this.locationFilterCountTarget.textContent = locationCount;
        this.locationFilterCountTarget.classList.remove('hidden');
      } else {
        this.locationFilterCountTarget.classList.add('hidden');
      }
    }
  }

  updateFilterStatus(visible, total, filterCount) {
    if (this.hasFilterStatusTarget) {
      if (filterCount > 0) {
        this.filterStatusTarget.textContent = `Showing ${visible} of ${total} jobs`;
      } else {
        this.filterStatusTarget.textContent = '';
      }
    }
  }
}
